import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TestContext } from "yup";
import { ProductDetailInfo } from "../ProductDetailPageTypeConfig";
import { Validation } from "../../localization/localeValidation";
import ajaxClient, { MaxServerError } from "../../../../js/common/AjaxClient";
import popupAlertService from "../../../../js/common/PopupAlertService";

interface ProductPriceRequestFormProperties {
    readonly productDetailInfo: ProductDetailInfo;
    readonly onSubmitted: (submittedEmail: string) => void;
}

interface SendPriceRequestResponse {
    email: string;
}

interface ProductPriceRequestFormContext {
    price: number;
}

const formSchema = yup.object({
    price: yup.number<number, ProductPriceRequestFormContext>()
        .typeError("Zadejte prosím částku nabídky.")
        .required("Zadejte prosím Vaši nabídku.")
        .test(
            "lowerThanPrice",
            "Zadaná částka musí být nižší, než je aktuální cena zboží.",
            (value, context: TestContext<ProductPriceRequestFormContext>) => context.options.context === undefined || value < context.options.context.price,
        ),
    email: yup.string()
        .lowercase()
        .trim()
        .required("Zadejte prosím Váš e-mail.")
        .matches(Validation.EmailRegEx, "Zadejte prosím platný e-mail"),
    agree: yup.boolean()
        .oneOf([true], "Musíte souhlasit se zasíláním obchodní nabídky.")
        .defined(),
});
type FormData = yup.InferType<typeof formSchema>;

const sendPriceRequest = async function(productDetailInfo: ProductDetailInfo, data: FormData): Promise<void> {
    try {
        await ajaxClient.post<SendPriceRequestResponse>("/Product/jsSendPriceRequest", {
            currentPrice: productDetailInfo.price,
            requestedPrice: data.price,
            productId: productDetailInfo.productId,
            email: data.email,
            ctrl1: "",
            ctrl2: "val2",
        });
    } catch (error) {
        if (error instanceof MaxServerError) {
            void popupAlertService.showAlert(error.message);

            return;
        }

        throw error;
    }
};

export default function ProductPriceRequestForm({
    productDetailInfo,
    onSubmitted,
}: ProductPriceRequestFormProperties): React.JSX.Element {
    const { register, handleSubmit, formState: { errors } } = useForm<FormData, ProductPriceRequestFormContext>({
        resolver: yupResolver(formSchema),
        context: { price: productDetailInfo.price },
    });
    const onSubmit: SubmitHandler<FormData> = async (data: FormData): Promise<void> => {
        await sendPriceRequest(productDetailInfo, data);
        onSubmitted(data.email);
    };

    return (
        <div className="modal-body shop-body make-offer-body">
            <form onSubmit={(event): void => void handleSubmit(onSubmit)(event)}>
                <p className="fakeh3">Chcete toto zboží koupit za jinou cenu?</p>
                <span
                    className="variant-identifier">{productDetailInfo.title}
                </span>
                <span> (varianta {productDetailInfo.productId})</span>
                <div className="shop-body-input-container">
                    <div className="make-offer-input-container">
                        <div className="make-offer-input">
                            <div className="input-container">
                                <input
                                    type="number" {...register("price")}
                                    placeholder="Zadejte nabízenou částku"
                                    id="rp_requestedPrice" />Kč
                            </div>
                            <label htmlFor="rp_requestedPrice" className="error">{errors.price?.message}</label>
                        </div>
                    </div>
                    <div className="make-offer-input-container">
                        <label>Zadejte Váš e-mail</label>
                        <div className="input-container">
                            <input
                                type="text" placeholder="Zadejte Váš e-mail" {...register("email")}
                                id="rp_email" />
                        </div>
                        <label htmlFor="rp_email" className="error">{errors.email?.message}</label>
                    </div>
                </div>
                <label className="form-group shop-body-checkbox">
                    <input type="checkbox" {...register("agree")} id="rp_service" />Souhlasím se zasíláním obchodní
                    nabídky.
                </label>
                <label htmlFor="rp_service" className="error" style={{ display: "block" }}>{errors.agree?.message}</label>
                <div className="text-right shop-body-button">
                    <button type="submit" className="btn btn-success">Odeslat návrh ceny</button>
                </div>
            </form>
        </div>
    );
}
